import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlert, useSpinner } from '../context';
import { ApplicationError, UnauthorizedError } from '../lib';

interface ReqOptions {
  /**
   * run in `finally` block
   */
  final?: () => void;
  /**
   * message displayed after the request succeeded
   */
  successMsg?: string;
  /**
   * redirect to login page when 401
   * @default true
   */
  redirectWhenAuthError?: boolean;
  /**
   * message displayed after the request failed, which overwriting the message received from server.
   *
   * if `redirectWhenAuthError` is `true`, `failureMsg` does not work when 401.
   */
  failureMsg?: string;
}

export function useTry() {
  const navigate = useNavigate();
  const { setLoading } = useSpinner();
  const { setError, setSuccess } = useAlert();

  const wrapReq = useCallback(
    // eslint-disable-next-line complexity
    (req: () => Promise<unknown>, options?: ReqOptions) => async () => {
      try {
        setLoading(true);
        await req();

        if (options?.successMsg) setSuccess(options.successMsg);
      } catch (e) {
        if (e instanceof UnauthorizedError && (options?.redirectWhenAuthError ?? true)) {
          navigate('/');
        } else if (e instanceof ApplicationError) {
          setError(options?.failureMsg ?? e.message);
        }
      } finally {
        options?.final?.();
        setLoading(false);
      }
    },
    [],
  );

  const tryReq = useCallback(async (...params: Parameters<typeof wrapReq>) => {
    await wrapReq(...params)();
  }, []);

  return {
    /**
     * syntax surger for `wrapReq(req)()`
     */
    tryReq,
    /**
     * Warp request into try-catch block.
     * Lock the screen during request and do error handling
     */
    wrapReq,
  };
}
